import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getPricing, updatePricing } from "@/lib/api";

const FREQ_KEYS = ["One-time", "Weekly", "Biweekly", "Monthly"];
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const Field = ({ label, value, onChange, type = "number", prefix, testid, w = "w-24" }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{label}</span>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-navy text-sm">{prefix}</span>}
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className={`h-10 bg-white ${prefix ? "pl-6" : ""} ${w}`} />
    </div>
  </label>
);

export default function PricingEditor({ token }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPricing().then(setCfg).catch(() => toast.error("Could not load pricing.")).finally(() => setLoading(false));
  }, []);

  const setBucket = (i, patch) => setCfg((c) => ({ ...c, price_table: c.price_table.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  const setStd = (i, key, v) => setCfg((c) => ({ ...c, price_table: c.price_table.map((b, idx) => (idx === i ? { ...b, standard: { ...b.standard, [key]: v } } : b)) }));
  const setExtra = (group, key, v) => setCfg((c) => ({ ...c, extra: { ...c.extra, [group]: { ...c.extra[group], [key]: v } } }));
  const setAddon = (i, patch) => setCfg((c) => ({ ...c, addons: c.addons.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }));
  const setPromise = (patch) => setCfg((c) => ({ ...c, recurring_promise: { ...c.recurring_promise, ...patch } }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...cfg,
      min_visit: num(cfg.min_visit),
      price_table: cfg.price_table.map((b) => ({
        ...b, maxSqft: num(b.maxSqft), incBed: num(b.incBed), incBath: num(b.incBath),
        deep: num(b.deep), moveout: num(b.moveout), startingAt: !!b.startingAt,
        standard: FREQ_KEYS.reduce((o, k) => ({ ...o, [k]: num(b.standard[k]) }), {}),
      })),
      extra: {
        standard: { bed: num(cfg.extra.standard.bed), bath: num(cfg.extra.standard.bath) },
        deep: { bed: num(cfg.extra.deep.bed), bath: num(cfg.extra.deep.bath) },
      },
      addons: cfg.addons.map((a) => ({ ...a, price: num(a.price) })),
      recurring_promise: {
        ...cfg.recurring_promise,
        items: (cfg.recurring_promise.items || []).map((s) => s.trim()).filter(Boolean),
        fineprint: (cfg.recurring_promise.fineprint || []).map((s) => s.trim()).filter(Boolean),
      },
    };
    try {
      const saved = await updatePricing(token, payload);
      setCfg(saved);
      toast.success("Pricing saved — it's now live on your Instant Quote tool.");
    } catch {
      toast.error("Save failed. Please check the values and try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-leaf" /></div>;

  return (
    <div className="space-y-8" data-testid="pricing-editor">
      <div className="flex items-center justify-between sticky top-16 z-10 bg-muted/40 py-3 -mt-3">
        <p className="text-sm text-muted-foreground max-w-xl">Edit your rates below. Saving updates the live Instant Quote tool immediately for all visitors.</p>
        <button onClick={save} disabled={saving} data-testid="pricing-save-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-leaf text-white font-medium hover:scale-[1.02] transition-transform disabled:opacity-60 shrink-0">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </button>
      </div>

      {/* Size buckets */}
      <section>
        <h2 className="font-heading text-xl font-semibold text-navy mb-1">Size buckets & base prices</h2>
        <p className="text-sm text-muted-foreground mb-4">House, townhouse, condo & apartment share these prices.</p>
        <div className="space-y-4">
          {cfg.price_table.map((b, i) => (
            <div key={i} className="bg-white rounded-2xl border border-border p-5" data-testid={`bucket-${i}`}>
              <div className="flex flex-wrap items-end gap-4 mb-4">
                <label className="block flex-1 min-w-[200px]">
                  <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Label</span>
                  <Input data-testid={`bucket-${i}-label`} value={b.label} onChange={(e) => setBucket(i, { label: e.target.value })} className="h-10 bg-white" />
                </label>
                <Field label="Max sq ft" value={b.maxSqft} onChange={(v) => setBucket(i, { maxSqft: v })} testid={`bucket-${i}-maxsqft`} w="w-28" />
                <Field label="Incl. beds" value={b.incBed} onChange={(v) => setBucket(i, { incBed: v })} testid={`bucket-${i}-incbed`} w="w-20" />
                <Field label="Incl. baths" value={b.incBath} onChange={(v) => setBucket(i, { incBath: v })} testid={`bucket-${i}-incbath`} w="w-20" />
                <label className="flex items-center gap-2 pb-2.5">
                  <input type="checkbox" checked={!!b.startingAt} onChange={(e) => setBucket(i, { startingAt: e.target.checked })} data-testid={`bucket-${i}-startingat`} className="w-4 h-4 accent-leaf" />
                  <span className="text-sm text-navy">"Starting at" price</span>
                </label>
              </div>
              <div className="flex flex-wrap gap-3">
                {FREQ_KEYS.map((k) => (
                  <Field key={k} label={`Std ${k}`} prefix="$" value={b.standard[k]} onChange={(v) => setStd(i, k, v)} testid={`bucket-${i}-std-${k}`} w="w-24" />
                ))}
                <Field label="Deep" prefix="$" value={b.deep} onChange={(v) => setBucket(i, { deep: v })} testid={`bucket-${i}-deep`} w="w-24" />
                <Field label="Move-in/out" prefix="$" value={b.moveout} onChange={(v) => setBucket(i, { moveout: v })} testid={`bucket-${i}-moveout`} w="w-24" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Extra rooms & minimum */}
      <section>
        <h2 className="font-heading text-xl font-semibold text-navy mb-4">Extra rooms & minimum</h2>
        <div className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4">
          <Field label="Extra bed (Std)" prefix="$" value={cfg.extra.standard.bed} onChange={(v) => setExtra("standard", "bed", v)} testid="extra-std-bed" />
          <Field label="Extra bath (Std)" prefix="$" value={cfg.extra.standard.bath} onChange={(v) => setExtra("standard", "bath", v)} testid="extra-std-bath" />
          <Field label="Extra bed (Deep/MIO)" prefix="$" value={cfg.extra.deep.bed} onChange={(v) => setExtra("deep", "bed", v)} testid="extra-deep-bed" />
          <Field label="Extra bath (Deep/MIO)" prefix="$" value={cfg.extra.deep.bath} onChange={(v) => setExtra("deep", "bath", v)} testid="extra-deep-bath" />
          <Field label="Minimum visit" prefix="$" value={cfg.min_visit} onChange={(v) => setCfg((c) => ({ ...c, min_visit: v }))} testid="min-visit" />
        </div>
      </section>

      {/* Add-ons */}
      <section>
        <h2 className="font-heading text-xl font-semibold text-navy mb-4">Add-ons</h2>
        <div className="bg-white rounded-2xl border border-border divide-y divide-border" data-testid="addons-editor">
          {cfg.addons.map((a, i) => (
            <div key={a.key} className="flex flex-wrap items-end gap-4 p-4">
              <label className="block flex-1 min-w-[200px]">
                <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Label</span>
                <Input data-testid={`addon-edit-${a.key}-label`} value={a.label} onChange={(e) => setAddon(i, { label: e.target.value })} className="h-10 bg-white" />
              </label>
              <Field label="Price" prefix="$" value={a.price} onChange={(v) => setAddon(i, { price: v })} testid={`addon-edit-${a.key}-price`} />
              <label className="block w-32">
                <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Unit</span>
                <Input data-testid={`addon-edit-${a.key}-unit`} value={a.unit} placeholder="e.g. each" onChange={(e) => setAddon(i, { unit: e.target.value })} className="h-10 bg-white" />
              </label>
            </div>
          ))}
        </div>
      </section>

      {/* Offer + promise */}
      <section>
        <h2 className="font-heading text-xl font-semibold text-navy mb-4">Offer & Recurring Promise</h2>
        <div className="bg-white rounded-2xl border border-border p-5 space-y-4">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">First-clean offer banner</span>
            <Input data-testid="offer-text" value={cfg.first_clean_offer} onChange={(e) => setCfg((c) => ({ ...c, first_clean_offer: e.target.value }))} className="h-10 bg-white" />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Promise title</span>
              <Input data-testid="promise-title" value={cfg.recurring_promise.title} onChange={(e) => setPromise({ title: e.target.value })} className="h-10 bg-white" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Promise intro</span>
              <Input data-testid="promise-intro" value={cfg.recurring_promise.intro} onChange={(e) => setPromise({ intro: e.target.value })} className="h-10 bg-white" />
            </label>
          </div>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Included items (one per line)</span>
            <textarea data-testid="promise-items" value={(cfg.recurring_promise.items || []).join("\n")} onChange={(e) => setPromise({ items: e.target.value.split("\n") })}
              rows={8} className="w-full rounded-xl border border-border bg-white p-3 text-sm text-navy" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Fine print (one per line)</span>
            <textarea data-testid="promise-fineprint" value={(cfg.recurring_promise.fineprint || []).join("\n")} onChange={(e) => setPromise({ fineprint: e.target.value.split("\n") })}
              rows={5} className="w-full rounded-xl border border-border bg-white p-3 text-sm text-navy" />
          </label>
        </div>
      </section>

      <div className="flex justify-end pb-4">
        <button onClick={save} disabled={saving} data-testid="pricing-save-btn-bottom"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-leaf text-white font-medium hover:scale-[1.02] transition-transform disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </button>
      </div>
    </div>
  );
}
